/**
 * PDF → uma imagem JPEG, com as páginas empilhadas.
 *
 * O canhoto é gravado pela rota do app do motorista (POST /entregas/:id/confirmar),
 * que só aceita JPG/PNG/WEBP. Para o escritório anexar o PDF que recebeu sem
 * mexer no backend, as páginas são desenhadas aqui no navegador.
 *
 * O pdf.js é importado sob demanda: são alguns MB que só baixam quando alguém
 * escolhe um PDF, não a cada abertura do back-office.
 */

// Canhoto é uma página; o limite só barra o PDF da nota inteira por engano.
const MAX_PAGINAS = 5;
// Largura boa para ler assinatura e carimbo sem estourar os 10 MB do upload.
const LARGURA_ALVO = 1600;
// Canvas muito alto falha em silêncio (sai em branco) em alguns navegadores.
const ALTURA_MAXIMA = 14000;

const canvasParaBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Não foi possível gerar a imagem do PDF.'))),
      'image/jpeg',
      0.85
    )
  );

export const pdfParaImagem = async (arquivo: File): Promise<File> => {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
  // Sem worker à parte: o entry põe o worker em `window.pdfjsWorker` e o pdf.js
  // roda na própria aba. Assim não é preciso servir o arquivo do worker nem
  // mexer na CSP — para uma página de canhoto, o custo é desprezível.
  await import('pdfjs-dist/legacy/build/pdf.worker.entry');

  const documento = await pdfjs.getDocument({
    data: new Uint8Array(await arquivo.arrayBuffer()),
    // Fecha a porta do CVE-2024-4367 (fonte maliciosa executando JS).
    isEvalSupported: false,
  }).promise;

  try {
    if (documento.numPages > MAX_PAGINAS) {
      throw new Error(
        `O PDF tem ${documento.numPages} páginas. Envie só a página do canhoto (até ${MAX_PAGINAS}).`
      );
    }

    const paginas = [];
    for (let numero = 1; numero <= documento.numPages; numero += 1) {
      paginas.push(await documento.getPage(numero));
    }

    const tamanhos = paginas.map((pagina) => pagina.getViewport({ scale: 1 }));
    const larguraBase = Math.max(...tamanhos.map((viewport) => viewport.width));
    const alturaBase = tamanhos.reduce((total, viewport) => total + viewport.height, 0);
    const escala = Math.min(LARGURA_ALVO / larguraBase, ALTURA_MAXIMA / alturaBase);

    const folha = document.createElement('canvas');
    folha.width = Math.ceil(larguraBase * escala);
    folha.height = Math.ceil(alturaBase * escala);
    const contexto = folha.getContext('2d');
    if (!contexto) throw new Error('Não foi possível gerar a imagem do PDF.');
    // JPEG não tem transparência: sem fundo, a margem da página sai preta.
    contexto.fillStyle = '#ffffff';
    contexto.fillRect(0, 0, folha.width, folha.height);

    // Cada página num canvas próprio: o pdf.js pinta o fundo do canvas INTEIRO
    // antes de desenhar, então renderizar direto na folha apagaria as anteriores.
    let topo = 0;
    for (const pagina of paginas) {
      const viewport = pagina.getViewport({ scale: escala });
      const canvasPagina = document.createElement('canvas');
      canvasPagina.width = Math.ceil(viewport.width);
      canvasPagina.height = Math.ceil(viewport.height);
      const contextoPagina = canvasPagina.getContext('2d');
      if (!contextoPagina) throw new Error('Não foi possível gerar a imagem do PDF.');
      await pagina.render({ canvasContext: contextoPagina, viewport }).promise;
      contexto.drawImage(canvasPagina, 0, topo);
      topo += canvasPagina.height;
    }

    const blob = await canvasParaBlob(folha);
    const nome = `${arquivo.name.replace(/\.pdf$/i, '') || 'canhoto'}.jpg`;
    return new File([blob], nome, { type: 'image/jpeg' });
  } finally {
    documento.destroy();
  }
};
